import * as React from 'react'
import { Platform } from 'react-native'

import MySitesScreen from './MySitesScreen'
import SiteScreen from './SiteScreen'
import PrestaSiteScreen from './presta/PrestaSiteScreen'
import OrgaSiteScreen from './orga/OrgaSiteScreen'
import ExpoHomeScreen from './expo/ExpoHomeScreen'
import MyManifestationsScreen from './MyManifestationsScreens'
import HotlineHomeScreen from './hotline/HotlineHomeScreen'

import api from '@api/api'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as AuthStore from '@store/auth/auth'
import { actions } from '@utils/useSiteChangeHandler'

import { AuthScope } from '@api/enum'

import push from '@utils/firebase/push'
import useDeeplink from '@utils/useDeeplink'
import useNavigation from '@layout/useNavigation'

import logger from '@utils/logger'

import { orderManifestations } from '@screens/manifestations/manifestationsUtils'

const HomeScreen = () => {
  const sites = useReducer(SiteStore.store, (s) => s.sites!)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const manifestation = useReducer(SiteStore.store, (s) => s.manifestation)
  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const user = useReducer(UserStore.store, (s) => s.user)

  const [status, setStatus] = React.useState<Status>('loading')

  const navigation = useNavigation()

  // handle deeplink
  useDeeplink()

  // find sites
  React.useEffect(() => {
    api.site
      .all()
      .then((res) => {
        SiteStore.actions.setSites(res)

        if (res.length === 1) {
          SiteStore.actions.setSite(res[0])
        } else if (res.length > 1) {
          setStatus('fetched')
        }
      })
      .catch((err) => {
        logger.error(err)
        actions.setStatus('error')
      })
  }, [])

  // manifestations
  React.useEffect(() => {
    if (user) {
      if (user.siteIds.length === 0 && user.manifestationIds.length > 0) {
        // no site id affected looking for manifestation
        api.manifestation.all().then((manifs) => {
          SiteStore.actions.setManifestations(orderManifestations(manifs))

          const userManifs = manifs.filter((m) => user.manifestationIds.includes(m.id))

          if (userManifs.length === 1) {
            // only 1 manifs selectable
            const manif = userManifs[0]

            SiteStore.actions.setManifestation(manif)
            api.site.get(manif.siteId).then(SiteStore.actions.setSite)
          }

          setStatus('fetched')
        })
      }
    }
  }, [user])

  React.useEffect(() => {
    if (user) {
      // trigger when user change

      if (Platform.OS !== 'web') {
        // only for mobile
        push.requestPermission().then((authorized) => {
          if (authorized) {
            push.configure(navigation)

            // if connected -> save token and register preferences
            push
              .getToken()
              .then((token) =>
                api.notifification.token({
                  tokenPush: token,
                  plateforme: Platform.OS === 'android' ? 'ANDROID' : 'IOS',
                })
              )
              .catch(logger.error)
          }
        })
      }

      api.notifification
        .getPreferences()
        .then((res) => UserStore.actions.setPreferences({ notifications: res }))
        .catch(logger.error)
    }
  }, [user])

  const onSiteBack = sites.length > 1 ? SiteStore.actions.resetSite : undefined
  const onManifBack =
    manifestations.inProgress.length + manifestations.incoming.length + manifestations.toBeClosed.length > 1
      ? SiteStore.actions.resetManifestation
      : undefined

  if (!site) {
    if (user && user.siteIds.length === 0 && user.manifestationIds.length > 0) {
      // no site associated, but manifs -> select manifestation
      return <MyManifestationsScreen status={status} />
    }

    return <MySitesScreen status={status} />
  }

  if (user) {
    if (scopes.includes(AuthScope.HomePersonaCoordinateur)) {
      return <SiteScreen onBack={onSiteBack} />
    }

    if (scopes.includes(AuthScope.HomePersonaHotline)) {
      return <HotlineHomeScreen onBack={onSiteBack} />
    }

    if (scopes.includes(AuthScope.HomePersonaPrestataire)) {
      return <PrestaSiteScreen onBack={onSiteBack} />
    }

    if (scopes.includes(AuthScope.HomePersonaOrganisateur)) {
      if (!manifestation) {
        return <MyManifestationsScreen status={status} onBack={onSiteBack} />
      }
      return <OrgaSiteScreen onBack={onManifBack} />
    }

    if (scopes.includes(AuthScope.HomePersonaExposant)) {
      if (!manifestation) {
        return <MyManifestationsScreen status={status} onBack={onSiteBack} />
      }
      return <ExpoHomeScreen onBack={onSiteBack} />
    }
  }

  return <SiteScreen onBack={onSiteBack} />
}

export default HomeScreen
